<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";

// Import GIFs
import gif1 from "@/assets/gifs/1.gif";
import gif2 from "@/assets/gifs/2.gif";
import gif3 from "@/assets/gifs/3.gif";
import gif4 from "@/assets/gifs/four.gif";
import gif5 from "@/assets/gifs/five.gif";
import gif6 from "@/assets/gifs/six.gif";

// Slider state
const currentSlide = ref(0);
const slides = [gif1, gif2, gif3, gif4, gif5, gif6];
let slideIntervalId = null;

// Left column state
const showLessee = ref(true);
let contentIntervalId = null;

// Steps with PrimeIcons
const lesseeSteps = [
  { icon: "pi pi-search", text: "Find the item you need" },
  { icon: "pi pi-shopping-cart", text: "Add it to your cart" },
  { icon: "pi pi-credit-card", text: "Pay securely" },
  { icon: "pi pi-box", text: "Pick up and return with ease" },
];
const lessorSteps = [
  { icon: "pi pi-list", text: "List the items you own" },
  { icon: "pi pi-wallet", text: "Add your payout details" },
  { icon: "pi pi-dollar", text: "Earn when others rent" },
];

const currentSteps = computed(() =>
  showLessee.value ? lesseeSteps : lessorSteps
);
const activeStep = ref(0);

// Typing effect
const typedText = ref("");
let typingTimeoutId = null;

const typeStepText = () => {
  typedText.value = "";
  const text = currentSteps.value[activeStep.value].text;
  let i = 0;
  const typeNext = () => {
    if (i <= text.length) {
      typedText.value = text.substring(0, i);
      i++;
      typingTimeoutId = setTimeout(typeNext, 80);
    }
  };
  typeNext();
};

let stepIntervalId = null;

onMounted(() => {
  slideIntervalId = setInterval(nextSlide, 12000);
  stepIntervalId = setInterval(() => {
    activeStep.value = (activeStep.value + 1) % currentSteps.value.length;
  }, 6000);
  contentIntervalId = setInterval(() => {
    showLessee.value = !showLessee.value;
    activeStep.value = 0;
  }, 180000);
  typeStepText();
});

onUnmounted(() => {
  clearInterval(slideIntervalId);
  clearInterval(contentIntervalId);
  clearInterval(stepIntervalId);
  clearTimeout(typingTimeoutId);
});

watch(activeStep, () => { clearTimeout(typingTimeoutId); typeStepText(); });
watch(showLessee, () => { clearTimeout(typingTimeoutId); typeStepText(); });

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.length;
};
const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + slides.length) % slides.length;
};
</script>

<template>
  <div class="py-2">
    <div class="bg-theme-card/20 border border-theme-border rounded-2xl p-5 sm:p-8">
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">

      <!-- LEFT COLUMN -->
      <div class="lg:col-span-2 space-y-5 text-center lg:text-left">
        <h1 class="text-3xl lg:text-4xl font-extrabold text-theme-text leading-tight">
          {{ showLessee ? "Renting Made Simple" : "Earn With Your Items" }}
        </h1>
        <p class="text-theme-text2 text-sm sm:text-base max-w-md mx-auto lg:mx-0 leading-relaxed">
          {{
            showLessee
              ? "Get the item you need quickly, easily, and affordably."
              : "Turn your unused items into income with a few simple steps."
          }}
        </p>

        <!-- Animated step card -->
        <transition name="step" mode="out-in">
          <div
            :key="currentSteps[activeStep].text"
            class="flex items-center gap-4 bg-theme-card border border-theme-border rounded-xl px-5 py-4 shadow-sm"
          >
            <span class="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center shrink-0">
              <i
                :class="[
                  currentSteps[activeStep].icon,
                  'text-xl text-primary-600'
                ]"
              ></i>
            </span>
            <span class="text-theme-text font-semibold text-base sm:text-lg">
              {{ typedText }}<span class="cursor"></span>
            </span>
          </div>
        </transition>

        <!-- Step dots -->
        <div class="flex items-center gap-2 justify-center lg:justify-start">
          <button
            v-for="(step, idx) in currentSteps"
            :key="idx"
            @click="activeStep = idx"
            class="transition-all duration-300 rounded-full cursor-pointer"
            :class="idx === activeStep
              ? 'w-6 h-2 bg-primary-600'
              : 'w-2 h-2 bg-theme-border hover:bg-theme-text2'"
          ></button>
        </div>
      </div>

      <!-- RIGHT COLUMN — Slider -->
      <div class="lg:col-span-3 relative group/slider">
        <div class="overflow-hidden rounded-xl">
          <div
            class="flex transition-transform duration-700 ease-in-out"
            :style="`transform: translateX(-${currentSlide * 100}%)`"
          >
            <div
              v-for="(slide, index) in slides"
              :key="index"
              class="flex-shrink-0 w-full h-72 flex items-center justify-center bg-theme-bg3/20"
            >
              <img
                class="max-h-full max-w-full object-contain"
                :src="slide"
                :alt="`Slide ${index + 1}`"
              />
            </div>
          </div>
        </div>

        <!-- Nav arrows -->
        <button
          @click="prevSlide"
          class="absolute top-1/2 left-2 -translate-y-1/2 w-8 h-8 rounded-full bg-theme-card/80 backdrop-blur-sm border border-theme-border/50 flex items-center justify-center text-theme-text2 hover:text-primary-600 hover:border-primary-600/30 shadow-sm opacity-0 group-hover/slider:opacity-100 transition-all duration-300 cursor-pointer"
        ><i class="pi pi-chevron-left text-xs"></i></button>
        <button
          @click="nextSlide"
          class="absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 rounded-full bg-theme-card/80 backdrop-blur-sm border border-theme-border/50 flex items-center justify-center text-theme-text2 hover:text-primary-600 hover:border-primary-600/30 shadow-sm opacity-0 group-hover/slider:opacity-100 transition-all duration-300 cursor-pointer"
        ><i class="pi pi-chevron-right text-xs"></i></button>

        <!-- Dots -->
        <div class="flex items-center justify-center gap-1.5 mt-2.5">
          <button
            v-for="(_, idx) in slides"
            :key="idx"
            @click="currentSlide = idx"
            class="transition-all duration-300 rounded-full cursor-pointer"
            :class="idx === currentSlide
              ? 'w-5 h-1.5 bg-primary-600'
              : 'w-1.5 h-1.5 bg-theme-border hover:bg-theme-text2'"
          ></button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
/* Step card transition */
.step-enter-active,
.step-leave-active {
  transition: all 0.4s ease;
}
.step-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.step-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Cursor blink */
.cursor {
  display: inline-block;
  width: 2px;
  height: 1.1em;
  background: var(--color-primary-600, #e68d00);
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: blink 0.75s step-end infinite;
}

@keyframes blink {
  50% { background: transparent; }
}
</style>
